import { Tab } from '@mui/material';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { useMatch } from 'react-router';
import { Outlet, useNavigate } from 'react-router-dom';

import { MainScreen } from '@/components/organisms/MainScreen';
import { FF_DASHBOARD_NOTIFICATION_SETTINGS } from '@/data/featureFlags';
import {
  UpdateUserPreferencesRequest,
  UserPreferences,
  UserPreferencesContextType,
} from '@/DTOs/userPreferences';
import { useIntegratorFeatureFlag } from '@/hooks/useFeatureFlags';
import { usePermissionsManager } from '@/hooks/usePermissionsManager';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import { useUserPreferences } from '@/hooks/useUserPreferences';
import { Permission } from '@/types/permissions';

enum SettingsTabs {
  users = 'users',
  loginAndSecurity = 'login-and-security',
  notifications = 'notifications',
  language = 'language',
  timeZone = 'time-zone',
  appearance = 'appearance',
}

const defaultPreferences: UserPreferences = {
  notifications: {
    transferStatus: {
      created: false,
      paymentSent: true,
      completed: true,
      failed: false,
    },
  },
  appearance: {
    theme: 'light',
  },
  language: 'en',
  timezone: 'America/New_York',
};

export const Settings: React.FC = (): React.ReactElement => {
  const tr = useTranslator();
  const navigate = useNavigate();
  const toast = useToast();
  const permissionsManager = usePermissionsManager();
  // TODO: eventually remove this
  const notificationsOn = useIntegratorFeatureFlag(FF_DASHBOARD_NOTIFICATION_SETTINGS, false);

  const match = useMatch<'tab', '/settings/:tab/*'>('/settings/:tab/*');
  const tab = React.useMemo(
    (): string => match?.params.tab ?? SettingsTabs.loginAndSecurity,
    [match],
  );

  const [userPreferences, updatePreferences, preferencesWorking] = useUserPreferences();

  const onTabChanged = React.useCallback(
    (_: React.SyntheticEvent, value: SettingsTabs): void => {
      navigate(value, { replace: true });
    },
    [navigate],
  );

  const handlePreferencesUpdate = React.useCallback(
    async (request: UpdateUserPreferencesRequest): Promise<void> => {
      try {
        await updatePreferences(request);
        toast.show(tr('SETTINGS_UPDATE_SUCCESS'), 'success');
      } catch {
        toast.show(tr('SETTINGS_UPDATE_ERROR'), 'error');
      }
    },
    [toast, tr, updatePreferences],
  );

  const outletContext = React.useMemo(
    (): UserPreferencesContextType => ({
      preferences: { ...defaultPreferences, ...userPreferences },
      update: handlePreferencesUpdate,
      loading: preferencesWorking,
    }),
    [handlePreferencesUpdate, preferencesWorking, userPreferences],
  );

  const managesUsers = React.useMemo(
    (): boolean => permissionsManager.userIsAuthorizedTo(Permission.MANAGE_USER),
    [permissionsManager],
  );

  const manageNotifications = React.useMemo(
    (): boolean =>
      permissionsManager.userIsAuthorizedTo(Permission.RECEIVE_TRANSACTION_STATUS_NOTIFICATIONS),
    [permissionsManager],
  );

  return (
    <MainScreen title={tr('SETTINGS')}>
      <Box sx={styles.content}>
        <Tabs value={tab} sx={styles.tabs} onChange={onTabChanged}>
          {/* Check if we need to render this tab */}
          {managesUsers && <Tab label={tr('MANAGE_USERS')} value={SettingsTabs.users} />}

          <Tab label={tr('LOGIN_AND_SECURITY')} value={SettingsTabs.loginAndSecurity} />

          {notificationsOn && manageNotifications && (
            <Tab label={tr('NOTIFICATIONS')} value={SettingsTabs.notifications} />
          )}

          <Tab label={tr('LANGUAGE')} value={SettingsTabs.language} />
          <Tab label={tr('TIME_ZONE')} value={SettingsTabs.timeZone} />
          <Tab label={tr('APPEARANCE')} value={SettingsTabs.appearance} />
        </Tabs>

        <Box sx={styles.tabContent}>
          <Outlet context={outletContext} />
        </Box>
      </Box>
    </MainScreen>
  );
};

const styles: Record<string, SystemStyleObject> = {
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    maxWidth: 1056,
    height: '100%',
    maxHeight: '100%',
  },
  tabContent: {
    flex: 1,
    minHeight: 0,
  },
  tabs: {
    '&::after': {
      height: '2px',
    },
    '& .MuiTab-root': {
      textTransform: 'none',
      fontSize: '1rem',
      fontWeight: 500,
      minWidth: 120,
      position: 'relative',
      color: 'custom.LightGray',
      '&.Mui-selected': {
        color: 'text.primary',
      },
    },
    '& .MuiTabs-indicator': {
      height: '2px',
      backgroundColor: 'text.primary',
      bottom: 0,
    },
  },
};
